import { UserRole } from './../enums/user-role';
import { Subject } from 'rxjs';
import { BaseApiService } from '../services/base-api.service';

export class UserProfile {
  private readonly _baseApiService: BaseApiService;
  private _applicationProductRoles: Array<string>;
  private _isApplicationAdmin: boolean;

  private _applicationProductRolesSubject: Subject<Array<string>> = new Subject<Array<string>>();
  private _isApplicationAdminSubject: Subject<boolean> = new Subject<boolean>();

  public constructor({ baseApiService }: {
    baseApiService: BaseApiService
  });
  public constructor({ applicationProductRoles, isApplicationAdmin }: {
    applicationProductRoles: Array<string>,
    isApplicationAdmin: boolean
  });
  public constructor({ baseApiService, applicationProductRoles, isApplicationAdmin }: {
    baseApiService?: BaseApiService,
    applicationProductRoles?: Array<string>,
    isApplicationAdmin?: boolean
  }) {
    if (baseApiService) {
      this._baseApiService = baseApiService;
    } else {
      this._applicationProductRoles = applicationProductRoles;
      this._isApplicationAdmin = isApplicationAdmin;
    }
  }

  public id: string;
  public tenantId: string;
  public displayName: string;
  public givenName: string;
  public surname: string;
  public role: string;
  public identityProviderType: number;

  public get applicationProductRoles(): Promise<Array<string>> {
    return new Promise<Array<string>>((resolve, reject) => {
      try {
        if (this._applicationProductRoles !== undefined) {
          resolve(this._applicationProductRoles);
        } else {
          this._applicationProductRolesSubject.subscribe({
            next: (result) => {
              resolve(result);
            },
            complete: () => {
              this._applicationProductRolesSubject = new Subject<any>();
            },
            error: (err) => {
              reject(err);
            }
          });

          if (this._applicationProductRolesSubject.observers.length === 1) {
            this._baseApiService.getCurrentSubscriptionsProducts()
              .then((products) => {
                const productCodes = products.map(product => product.code);
                this._applicationProductRoles = productCodes;
                this._applicationProductRolesSubject.next(productCodes);
              })
              .catch((err) => {
                this._applicationProductRoles = null;
                this._applicationProductRolesSubject.error(err);
              })
              .finally(() => {
                this._applicationProductRolesSubject.complete();
              });
          }
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  public get isApplicationAdmin(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        if (this.role === UserRole.Admin) {
          resolve(true);
        } else if (this._isApplicationAdmin !== undefined) {
          resolve(this._isApplicationAdmin);
        } else {
          this._isApplicationAdminSubject.subscribe({
            next: (result) => {
              resolve(result);
            },
            complete: () => {
              this._isApplicationAdminSubject = new Subject<any>();
            },
            error: (err) => {
              reject(err);
            }
          });

          if (this._isApplicationAdminSubject.observers.length === 1) {
              this._baseApiService.isUserApplicationAdmin()
              .then((result) => {
                this._isApplicationAdmin = result;
                this._isApplicationAdminSubject.next(result);
              })
              .catch((err) => {
                this._isApplicationAdmin = null;
                this._isApplicationAdminSubject.error(err);
              })
              .finally(() => {
                this._isApplicationAdminSubject.complete();
              });
            }
          }
      } catch (err) {
        reject(err);
      }
    });
  }

  public get isApplicationAdminAssigned(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) =>{
      try {
        if (this.role === UserRole.Admin) {
          resolve(true);
        } else {
          this._isApplicationAdminSubject.subscribe({
            next: (result) => {
              resolve(result);
            },
            complete: () => {
              this._isApplicationAdminSubject = new Subject<any>();
            },
            error: (err) => {
              reject(err);
            }
          });

          if (this._isApplicationAdminSubject.observers.length === 1) {
              this._baseApiService.isUserApplicationAdminAssigned()
              .then((result) => {
                this._isApplicationAdmin = result;
                this._isApplicationAdminSubject.next(result);
              })
              .catch((err) => {
                this._isApplicationAdmin = null;
                this._isApplicationAdminSubject.error(err);
              })
              .finally(() => {
                this._isApplicationAdminSubject.complete();
              });
            }
          }
      } catch (err) {
        reject(err);
      }
    })
  }

  public isProductAuthorized(applicationProductCode: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.applicationProductRoles.then((roles) => {
        resolve(roles && roles.some(role => role.toUpperCase() === applicationProductCode.toUpperCase()));
      }).catch((err) => {
        reject(err);
      });
    });
  }
}
